import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import PhoneOtpField from "../components/PhoneOtpField";
import { Gavel, Users, Sparkles, Package, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  {
    id: "buyer",
    icon: Gavel,
    title: "I'm a Buyer",
    tagline: "I source print & packaging for my brand",
    bullets: [
      "Post sealed RFQs — printers bid anonymously",
      "Weighted scoring picks the best offer for you",
      "Escrow protection until you approve delivery",
    ],
  },
  {
    id: "printer",
    icon: Sparkles,
    title: "I'm a Printer / Supplier",
    tagline: "I want to bid on production auctions",
    bullets: [
      "See only jobs that match your press & substrate",
      "Submit one sealed bid per auction",
      "Fast-track payouts after delivery",
    ],
  },
  {
    id: "agency",
    icon: Users,
    title: "I'm a Design Agency",
    tagline: "I create packaging artwork for brands",
    bullets: [
      "Get matched to brief-driven design briefs",
      "Escrow-protected design fees on every job",
      "Portfolio & 3D dieline showcase",
    ],
  },
  {
    id: "material_supplier",
    icon: Package,
    title: "I'm a Material Supplier",
    tagline: "I supply paper, board, inks or films to printers",
    bullets: ["Quote on printers' material sub-RFQs", "Escrow-protected payment on every order", "Commission from 8% down to 4% by plan"],
  },
  {
    id: "finishing_partner",
    icon: Layers,
    title: "I'm a Finishing Partner",
    tagline: "I do lamination, foiling, die-cutting & more",
    bullets: ["Quote on printers' finishing sub-RFQs", "Escrow-protected payment on every order", "Commission from 8% down to 4% by plan"],
  },
];

export default function GoogleRolePicker() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneToken, setPhoneToken] = useState("");

  // If they landed here but role is already set, skip
  if (user === false) return <Navigate to="/login" replace />;
  if (user && user !== false && !user.role_pending) return <Navigate to="/workspace" replace />;

  const submit = async () => {
    if (!picked) { setErr("Please pick one role to continue."); return; }
    if (!phoneToken) { setErr("Please verify your mobile number with OTP."); return; }
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/auth/google/choose-role`, { role: picked, company, phone, phone_token: phoneToken });
      await refresh();
      nav("/workspace", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not set role. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell" data-testid="google-role-page">
      <div className="auth-card wide" style={{ maxWidth: 780 }}>
        <img src="/logo.png" alt="GoPrintPack" className="auth-logo" style={{ margin: "0 auto 12px", display: "block" }}/>
        <h1>Welcome, {user?.name?.split(" ")[0] || "there"}!</h1>
        <p className="auth-sub">One quick step — pick the account type that matches how you'll use GoPrintPack™. You can only pick this once.</p>

        <div className="role-grid" data-testid="role-grid">
          {ROLES.map(r => {
            const Icon = r.icon;
            const selected = picked === r.id;
            return (
              <button
                type="button"
                key={r.id}
                data-testid={`role-card-${r.id}`}
                className={`role-card ${selected ? "selected" : ""}`}
                onClick={() => setPicked(r.id)}
              >
                <div className="role-icon"><Icon size={22} /></div>
                <b>{r.title}</b>
                <span className="role-tag">{r.tagline}</span>
                <ul>
                  {r.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </button>
            );
          })}
        </div>

        <div className="role-extra">
          <label>Company (optional)
            <input data-testid="role-company" value={company} onChange={e => setCompany(e.target.value)} placeholder="Your business name" />
          </label>
          <PhoneOtpField testIdPrefix="role-otp" phone={phone} setPhone={setPhone} token={phoneToken} setToken={setPhoneToken} email={user?.email || ""} />
        </div>

        {err && <div className="auth-error" data-testid="role-error" style={{ marginTop: 14 }}>{err}</div>}

        <button
          data-testid="role-continue"
          className="primary-btn full"
          disabled={busy || !picked || !phoneToken}
          onClick={submit}
          style={{ marginTop: 18 }}
        >
          {busy ? "Setting up your workspace…" : (picked ? `Continue as ${picked}` : "Pick an account type")} →
        </button>
      </div>
    </div>
  );
}
